
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AnimatedPage from "@/components/AnimatedPage";
import ChitPoolCard from "@/components/ChitPoolCard";
import { Plus, Search, Grid, List, Edit } from "lucide-react";
import { ChitPool } from "@/lib/types";
import { motion } from "framer-motion";
import { getChitPools } from "@/lib/api";
import { toast } from "sonner";
import NavHeader from "@/components/NavHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { format, differenceInMonths } from "date-fns";

const ChitPools = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [chitPools, setChitPools] = useState<ChitPool[]>([]);
  const [filteredPools, setFilteredPools] = useState<ChitPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<"card" | "list">("card");
  
  useEffect(() => {
    const fetchChitPools = async () => {
      try {
        const pools = await getChitPools();
        setChitPools(pools);
        setFilteredPools(pools);
      } catch (error) {
        console.error("Error fetching chit pools:", error);
        toast.error("Failed to load chit pools.");
        setChitPools([]);
        setFilteredPools([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChitPools();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredPools(chitPools);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = chitPools.filter(
        (pool) => pool.name.toLowerCase().includes(query)
      );
      setFilteredPools(filtered);
    }
  }, [searchQuery, chitPools]);

  const handleViewToggle = () => {
    setViewType(viewType === "card" ? "list" : "card");
  };

  // Calculate months completed based on first due date
  const calculateMonthsCompleted = (firstDueDate: string) => {
    const startDate = new Date(firstDueDate);
    const currentDate = new Date();
    
    // If the due date is in the future, return 0
    if (startDate > currentDate) return 0;
    
    // Calculate months difference
    return differenceInMonths(currentDate, startDate);
  };

  if (loading) {
    return (
      <AnimatedPage>
        <div className="container max-w-6xl py-8 flex justify-center items-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading chit pools...</p>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="container max-w-6xl py-8 space-y-8">
        <NavHeader title="Chit Pools" subtitle="Manage your chit pools and members" />
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search chit pools..."
              className="pl-10 bg-background"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleViewToggle}
              title={viewType === "card" ? "Switch to list view" : "Switch to card view"}
            >
              {viewType === "card" ? (
                <List className="h-4 w-4" />
              ) : (
                <Grid className="h-4 w-4" />
              )}
            </Button>
            <Button onClick={() => navigate("/chit-pools/new")} className="whitespace-nowrap">
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">New Chit Pool</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {filteredPools.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 space-y-4"
          >
            <h3 className="text-lg font-medium">No chit pools found</h3>
            <p className="text-muted-foreground">
              {chitPools.length === 0
                ? "Get started by creating your first chit pool"
                : "No chit pools match your search criteria"}
            </p>
            {chitPools.length === 0 && (
              <Button
                onClick={() => navigate("/chit-pools/new")}
                className="mt-4"
                variant="outline"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Chit Pool
              </Button>
            )}
          </motion.div>
        ) : viewType === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPools.map((pool, index) => (
              <ChitPoolCard key={pool.id} chitPool={pool} index={index} />
            ))}
          </div>
        ) : (
          <Card className="bg-card/60 backdrop-blur-sm border border-border/50 overflow-x-auto">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Amount</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead className="hidden lg:table-cell">First Due Date</TableHead>
                    <TableHead className="hidden md:table-cell">Monthly Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPools.map((pool) => {
                    const monthsCompleted = calculateMonthsCompleted(pool.firstDueDate);
                    return (
                      <TableRow 
                        key={pool.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/chit-pools/${pool.id}`)}
                      >
                        <TableCell className="font-medium">
                          <div>
                            <div>{pool.name}</div>
                            <div className="sm:hidden text-sm text-muted-foreground">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(pool.amount)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(pool.amount)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {Math.min(monthsCompleted, pool.months)}/{pool.months}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{format(new Date(pool.firstDueDate), "dd/MM/yyyy")}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(pool.monthlyAmount)}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/chit-pools/${pool.id}/edit`);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AnimatedPage>
  );
};

export default ChitPools;
